import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { ensureCatalogToken, getAdminAuthToken } from '../admin/jscontrol/apiClient';
import {
  fetchSettingsMap,
  formatPriceWithSymbol,
  getSettingValue,
} from '../utils/settingsStorefront';

const DEFAULT_CURRENCY = '$';

const SettingsContext = createContext({
  currencySymbol: DEFAULT_CURRENCY,
  settings: {},
  formatPrice: (amount, decimals) => formatPriceWithSymbol(amount, DEFAULT_CURRENCY, decimals),
  refreshSettings: () => Promise.resolve(),
});

export function SettingsProvider({ children, catalogReady = true }) {
  const [settings, setSettings] = useState({});
  const [currencySymbol, setCurrencySymbol] = useState(DEFAULT_CURRENCY);

  const applySettings = useCallback((map) => {
    setSettings(map);
    const currency = getSettingValue(map, 'Currency', DEFAULT_CURRENCY);
    setCurrencySymbol(currency || DEFAULT_CURRENCY);
  }, []);

  const refreshSettings = useCallback(async () => {
    if (!getAdminAuthToken()) {
      ensureCatalogToken();
    }
    const map = await fetchSettingsMap();
    applySettings(map);
    return map;
  }, [applySettings]);

  useEffect(() => {
    if (!catalogReady) return;
    refreshSettings();
  }, [catalogReady, refreshSettings]);

  const formatPrice = useCallback(
    (amount, decimals = 2) => formatPriceWithSymbol(amount, currencySymbol, decimals),
    [currencySymbol]
  );

  return (
    <SettingsContext.Provider
      value={{ currencySymbol, settings, formatPrice, refreshSettings }}
    >
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}

export default SettingsContext;
